import React, { useState, useEffect } from "react";
import "./LoyaltyOfferFilterBar.css";
import { useTranslation } from "react-i18next";

const locations = [
  "All",
  "United Kingdom",
  "Italy",
  "France",
  "Monaco",
  "Israel",
  "United Arab Emirates",
  "Cyprus",
  "Worldwide",
];

// Category mapping: display name -> translation key
const categoryMap = {
  All: "All",
  Accommodation: "accommodation",
  "Art and Culture": "artAndCulture",
  Beauty: "beauty",
  Concierge: "concierge",
  Delivery: "delivery",
  Education: "education",
  Entertainment: "entertainment",
  Finance: "finance",
  "Food and Beverages": "foodAndBeverages",
  Health: "health",
  Immigration: "immigration",
  Insurance: "insurance",
  Legal: "legal",
  "Real Estate": "realEstate",
  Recruitment: "recruitment",
  "Executive Recruitment": "executiveRecruitment",
  Restaurants: "restaurants",
  Services: "services",
  Tax: "tax",
  Transport: "transport",
  Travel: "travel",
  Wellness: "wellness",
  Other: "other",
};

const categories = Object.keys(categoryMap);

export default function LoyaltyOfferFilterBar({
  filters,
  onFilterChange,
  searchQuery,
  onSearchChange,
  totalOffers,
  filteredCount,
}) {
  const { t } = useTranslation();
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Calculate active filters
  useEffect(() => {
    const count = Object.values(filters).filter(
      (value) => value && value !== ""
    ).length;
    const searchActive = searchQuery && searchQuery.trim() !== "";
    setActiveFiltersCount(count + (searchActive ? 1 : 0));
  }, [filters, searchQuery]);

  const handleChange = (key, value) => {
    const newValue = value === "All" ? "" : value;
    onFilterChange({ ...filters, [key]: newValue });
  };

  const handleSearchChange = (e) => {
    onSearchChange(e.target.value);
  };

  const clearAllFilters = () => {
    onFilterChange({ location: "", category: "" });
    onSearchChange("");
  };

  const hasActiveFilters = activeFiltersCount > 0;

  return (
    <div className="loyalty-filter-bar">
      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder={t("loyaltyProgram.searchPlaceholder")}
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
            aria-label="Search offers"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Filters Row */}
      <div className="filters-row">
        <div className="filters-section">
          <div
            className={`filter-group ${filters.location ? "has-selection" : ""}`}
          >
            <label htmlFor="location-select">{t("filter-location")}:</label>
            <div className="select-wrapper">
              <select
                id="location-select"
                value={filters.location || "All"}
                onChange={(e) => handleChange("location", e.target.value)}
                aria-label="Filter by location"
              >
                {locations.map((location) => (
                  <option
                    key={location}
                    value={location}
                  >
                    {location}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className={`filter-group ${filters.category ? "has-selection" : ""}`}
          >
            <label htmlFor="category-select">{t("filter-category")}:</label>
            <div className="select-wrapper">
              <select
                id="category-select"
                value={filters.category || "All"}
                onChange={(e) => handleChange("category", e.target.value)}
                aria-label="Filter by category"
              >
                {categories.map((category) => {
                  const translationKey = categoryMap[category];
                  let displayText;
                  if (category === "All") {
                    displayText =
                      t("participants_filters_allCategories") || "All";
                  } else {
                    // Use the same path as B2B components: auth.businessCategories.xxx
                    displayText =
                      t(`auth.businessCategories.${translationKey}`) ||
                      category;
                  }
                  return (
                    <option
                      key={category}
                      value={category}
                    >
                      {displayText}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Actions and Results */}
      <div className="actions-results-row">
        {hasActiveFilters && (
          <div className="filter-actions">
            <button
              className="clear-filters-btn"
              onClick={clearAllFilters}
              aria-label="Clear all filters"
            >
              {t("loyaltyProgram.clearFilters")}
              {activeFiltersCount > 0 && (
                <span className="filter-count">{activeFiltersCount}</span>
              )}
            </button>
          </div>
        )}

        {/* Results counter */}
        {filteredCount !== undefined && totalOffers !== undefined && (
          <div className="results-counter">
            <span className="results-text">
              {hasActiveFilters
                ? t("loyaltyProgram.showingFiltered", { filtered: filteredCount, total: totalOffers })
                : t("loyaltyProgram.showingAll", { total: totalOffers })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function filterOffers(offers, filters) {
  return offers.filter((offer) => {
    if (filters.location && filters.location !== "") {
      const locationMatch = offer.location.includes(filters.location);
      if (!locationMatch) return false;
    }

    if (filters.category && filters.category !== "") {
      const categoryMatch = offer.category.includes(filters.category);
      if (!categoryMatch) return false;
    }

    return true;
  });
}

export function searchOffers(offers, searchQuery) {
  if (!searchQuery || searchQuery.trim() === "") {
    return offers;
  }

  const query = searchQuery.toLowerCase().trim();

  return offers.filter((offer) => {
    // Search in name
    if (offer.name && offer.name.toLowerCase().includes(query)) {
      return true;
    }

    // Search in subtitle
    if (offer.subtitle && offer.subtitle.toLowerCase().includes(query)) {
      return true;
    }

    // Search in description
    if (offer.description && offer.description.toLowerCase().includes(query)) {
      return true;
    }

    // Search in category
    if (
      offer.category &&
      offer.category.some((cat) => cat.toLowerCase().includes(query))
    ) {
      return true;
    }

    // Search in location
    if (
      offer.location &&
      offer.location.some((loc) => loc.toLowerCase().includes(query))
    ) {
      return true;
    }

    // Search in offers
    const offerTexts = [
      offer.offerOne,
      offer.offerTwo,
      offer.offerThree,
      offer.offerFour,
      offer.offerFive,
    ];
    if (
      offerTexts.some(
        (offerText) => offerText && offerText.toLowerCase().includes(query)
      )
    ) {
      return true;
    }

    return false;
  });
}
